import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import styled, { keyframes, css } from 'styled-components';
import { FiClock, FiCheck } from 'react-icons/fi';
import { theme } from '../styles/theme';

// Pulsing animation for error state
const errorPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.5);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    border-color: rgba(239, 68, 68, 0.5);
  }
`;

// Styled Components
const DurationSelectorContainer = styled(motion.div)`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const DurationCard = styled(motion.button)`
  background: ${({ $hasError, $selected, theme }) =>
    $hasError
      ? theme.colors.background.card
      : $selected
        ? theme.colors.background.card
        : theme.colors.background.card};
  border: 2px solid ${({ $hasError, $selected, theme }) =>
    $hasError
      ? theme.colors.border.error
      : $selected
        ? theme.colors.success
        : theme.colors.border.default};
  opacity: 1;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  transition: all ${({ theme }) => theme.transitions.normal};
  position: relative;
  overflow: hidden;
  width: 100%;
  text-align: left;
  box-shadow: ${({ theme }) => theme.shadows.subtle};

  ${({ $hasError }) => $hasError && css`
    animation: ${errorPulse} 2s infinite ease-in-out;
  `}

  &:focus-visible {
    outline: none;
    border-color: ${({ $hasError, theme }) =>
    $hasError
      ? theme.colors.text.error
      : theme.colors.primary};
    box-shadow: ${({ $hasError, theme }) =>
    $hasError
      ? `0 0 0 3px ${theme.colors.border.error}`
      : `0 0 0 3px ${theme.colors.success}`};
  }

  &:hover {
    border-color: ${({ $selected, $hasError, theme }) =>
    $hasError
      ? theme.colors.border.error
      : $selected
        ? theme.colors.success
        : theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const DurationIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.gradient.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const DurationDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const DurationText = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin-bottom: 4px;
`;

const DurationSubtext = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const PlaceholderText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 2px;
`;

const PlaceholderSubtext = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.2;
`;

const Checkmark = styled(motion.div)`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.success};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
`;

const EmptyCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.background.card};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  flex-shrink: 0;
`;

// Popup Components
const PopupOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85); /* More opaque for better coverage */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  @media (max-width: 767px) {
    align-items: flex-end;
    padding: 0;
    background: rgba(15, 23, 42, 1); /* Fully opaque on mobile */
  }
`;

const PopupContainer = styled(motion.div)`
  background: ${({ theme }) => theme.colors.background.card};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 600px; /* Aligned with main app card */
  width: 90%;
  box-shadow: ${({ theme }) => theme.shadows.card};
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  max-height: 90vh; /* Prevent overflow on mobile */
  display: flex;
  flex-direction: column;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.colors.gradient.primary};
    border-radius: ${({ theme }) => theme.borderRadius.xl} ${({ theme }) => theme.borderRadius.xl} 0 0;
  }

  @media (max-width: 767px) {
    width: 100%;
    max-width: 100%;
    max-height: 100vh;
    max-height: 100dvh; /* Use dynamic viewport height for mobile */
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: ${({ theme }) => theme.borderRadius.xl};
    border-top-right-radius: ${({ theme }) => theme.borderRadius.xl};
    padding: ${({ theme }) => theme.spacing.lg};
    margin: 0;
  }
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
`;

const PopupTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0;
  text-align: center;
  flex: 1;
`;

const PopupDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: 0 0 24px 0;
  line-height: 1.5;
  opacity: 0.8;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const SuggestionsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  position: relative;
  z-index: 1;
`;

const SuggestionButton = styled(motion.button)`
  background: ${({ theme }) => theme.colors.background.card};
  border: 2px solid ${({ $isSelected, theme }) =>
    $isSelected
      ? theme.colors.success
      : theme.colors.border.default};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
  z-index: 1;
  box-shadow: ${({ theme }) => theme.shadows.subtle};

  &:hover {
    border-color: ${({ $isSelected, theme }) =>
    $isSelected
      ? theme.colors.success
      : theme.colors.primary};
    box-shadow: ${({ $isSelected, theme }) =>
    $isSelected
      ? theme.shadows.subtle
      : theme.shadows.prominent};
    transform: translateY(-2px);
  }
`;

const SuggestionCheckmark = styled(motion.div)`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.success};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
`;

const SuggestionValue = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin-bottom: 4px;
`;

const SuggestionDescription = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const DurationDisplay = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.background.card};
  border: 2px solid ${({ $hasDuration, $isCustom, $isValid, theme }) => {
    if ($isCustom && !$isValid) return theme.colors.text.error;
    if ($hasDuration || ($isCustom && $isValid)) return theme.colors.success;
    return theme.colors.border.default;
  }};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  gap: 12px;
  min-height: 48px;
  width: 100%;
  box-sizing: border-box;
`;

const DurationDisplayIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $hasDuration, $isCustom, $isValid, theme }) => {
    if ($isCustom && !$isValid) return theme.colors.text.error;
    if ($hasDuration) return theme.colors.success;
    return theme.colors.text.muted;
  }};
  font-size: 18px;
`;

const DurationDisplayText = styled.span`
  color: ${({ $hasDuration, theme }) => $hasDuration ? theme.colors.text.primary : 'transparent'};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: 400;
  flex: 1;
`;

const CustomDurationInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  box-shadow: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: 500;
  flex: 1;
  min-width: 0;
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const PopupActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  z-index: 1;
  width: 100%;
`;

const PopupButton = styled(motion.button)`
  width: 100%;
  padding: 14px 24px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  ${({ $primary, theme }) => $primary ? `
    background: ${theme.colors.gradient.primary};
    color: white;
    border: none;
    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
      filter: brightness(1.1);
    }
  ` : `
    background: transparent;
    color: ${theme.colors.text.error};
    border: 1px solid rgba(239, 68, 68, 0.4);
    
    &:hover:not(:disabled) {
      background: rgba(239, 68, 68, 0.05);
      border-color: ${theme.colors.text.error};
      transform: translateY(-1px);
    }
  `}
  
  &:active:not(:disabled) {
    transform: translateY(0);
    filter: brightness(0.9);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const DURATION_OPTIONS = [
  { value: '30', label: '30 Days', description: 'Standard Tourist' },
  { value: '90', label: '90 Days', description: 'Extended Stay' },
  { value: '180', label: '180 Days', description: 'Long Term' },
  { value: 'custom', label: 'Custom', description: 'Enter manually' }
];

const DurationSelectorComponent = ({
  visaDuration,
  onDurationChange,
  currentStep,
  completedSteps,
  externalOpen = false,
  onPopupClose
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [tempDuration, setTempDuration] = useState(null);
  const [customValue, setCustomValue] = useState('');
  const [isCustomMode, setIsCustomMode] = useState(false);

  // Handle external open request

  const handleOpenPopup = useCallback(() => {
    setTempDuration(visaDuration);
    if (visaDuration && !['30', '90', '180'].includes(visaDuration)) {
      setIsCustomMode(true);
      setCustomValue(visaDuration);
    } else {
      setIsCustomMode(false);
      setCustomValue('');
    }
    setShowPopup(true);
  }, [visaDuration]);

  useEffect(() => {
    if (externalOpen) {
      handleOpenPopup();
    }
  }, [externalOpen, handleOpenPopup]);

  const handleClosePopup = () => {
    setShowPopup(false);
    setTempDuration(null);
    setCustomValue('');
    setIsCustomMode(false);
    if (onPopupClose) onPopupClose();
  };

  const handleConfirm = () => {
    if (isCustomMode && customValue) {
      onDurationChange(customValue);
    } else if (tempDuration) {
      onDurationChange(tempDuration);
    }
    setShowPopup(false);
    if (onPopupClose) onPopupClose();
  };

  const handleOptionSelect = (value) => {
    if (value === 'custom') {
      setIsCustomMode(true);
      setTempDuration(null);
    } else {
      setIsCustomMode(false);
      setTempDuration(value);
    }
  };

  const handleCustomInputChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setCustomValue(val);
    if (val) {
      setTempDuration(val);
    } else {
      setTempDuration(null);
    }
  };

  const getDisplayDuration = () => {
    if (!visaDuration) return '';
    return `${visaDuration} Days`;
  };

  return (
    <DurationSelectorContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      <DurationCard
        $selected={!!visaDuration}
        $hasError={!visaDuration}
        onClick={handleOpenPopup}
        type="button"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        {visaDuration ? (
          <>
            <DurationIcon>
              <FiClock size={20} />
            </DurationIcon>
            <DurationDetails>
              <DurationText>{getDisplayDuration()}</DurationText>
              <DurationSubtext>Validity Period</DurationSubtext>
            </DurationDetails>
            <Checkmark
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <FiCheck size={12} />
            </Checkmark>
          </>
        ) : (
          <>
            <DurationIcon>
              <FiClock size={20} />
            </DurationIcon>
            <DurationDetails>
              <PlaceholderText>Select Duration</PlaceholderText>
              <PlaceholderSubtext>Enter visa validity in days</PlaceholderSubtext>
            </DurationDetails>
            <EmptyCircle />
          </>
        )}
      </DurationCard>

      {/* Required Field Message */}
      {!visaDuration && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            color: theme.colors.text.error,
            fontSize: '14px',
            marginTop: '8px',
            marginBottom: '48px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <div style={{
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            background: theme.colors.text.error
          }} />
          Please enter a valid visa duration to continue
        </motion.div>
      )}

      {/* Duration Popup */}
      {showPopup && ReactDOM.createPortal(
        <PopupOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClosePopup}
        >
          <PopupContainer
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <PopupHeader>
              <PopupTitle>Select Duration</PopupTitle>
            </PopupHeader>

            <PopupDescription>
              Select the number of days that the visa is valid for
            </PopupDescription>

            <SuggestionsGrid>
              {DURATION_OPTIONS.map((option) => (
                <SuggestionButton
                  key={option.value}
                  onClick={() => handleOptionSelect(option.value)}
                  $isSelected={
                    option.value === 'custom'
                      ? isCustomMode
                      : tempDuration === option.value && !isCustomMode
                  }
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SuggestionValue>
                    {option.value === 'custom' ? <FiClock /> : option.label}
                  </SuggestionValue>
                  <SuggestionDescription>{option.description}</SuggestionDescription>
                  {(option.value === 'custom' ? isCustomMode : (tempDuration === option.value && !isCustomMode)) && (
                    <SuggestionCheckmark
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FiCheck size={10} />
                    </SuggestionCheckmark>
                  )}
                </SuggestionButton>
              ))}
            </SuggestionsGrid>

            <DurationDisplay
              $hasDuration={!!tempDuration || (isCustomMode && !!customValue)}
              $isCustom={isCustomMode}
              $isValid={!!customValue}
            >
              <DurationDisplayIcon $hasDuration={!!tempDuration || (isCustomMode && !!customValue)} $isCustom={isCustomMode} $isValid={!!customValue}>
                {(tempDuration || (isCustomMode && customValue)) ? <FiCheck size={18} color={theme.colors.success} /> : <FiClock />}
              </DurationDisplayIcon>
              {isCustomMode ? (
                <CustomDurationInput
                  autoFocus
                  placeholder="Enter number of days"
                  value={customValue}
                  onChange={handleCustomInputChange}
                  type="tel"
                />
              ) : (
                <DurationDisplayText $hasDuration={!!tempDuration}>
                  {tempDuration ? `${tempDuration} Days` : 'Select a duration'}
                </DurationDisplayText>
              )}
              {(tempDuration || (isCustomMode && customValue)) && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    marginLeft: 'auto',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: theme.colors.success,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FiCheck size={12} />
                </motion.div>
              )}
            </DurationDisplay>

            <PopupActions>
              <PopupButton
                $primary
                onClick={handleConfirm}
                disabled={!tempDuration && (!isCustomMode || !customValue)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Confirm
              </PopupButton>
              <PopupButton
                onClick={handleClosePopup}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </PopupButton>
            </PopupActions>
          </PopupContainer>
        </PopupOverlay>,
        document.body
      )}
    </DurationSelectorContainer>
  );
};


// Export memoized version to prevent unnecessary re-renders
export const DurationSelector = React.memo(DurationSelectorComponent);
